<template>
  <div>
    <div v-for="weekday in WEEKDAYS" :key="weekday">
      <div class="flex gap-3 pt-5 place-items-center h-12">
        <span class="w-24 block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{ capitalize(weekday) }}</span>
        <fwb-checkbox
          v-model="proxyModel[weekday].isOpen"
          label="Is Open"
          @update:model-value="onChangeOpenState(weekday)" />
        <template v-if="proxyModel[weekday].isOpen">
          <fwb-checkbox
            v-model="proxyModel[weekday].allDay"
            label="24h"
            @update:model-value="onChangeAllDayState(weekday)" />

          <template v-if="!proxyModel[weekday].allDay">
            <fwb-select
              v-model="proxyModel[weekday].from"
              :options="buildHoursStart(weekday)"
              size="sm"
              class="w-28"
              @update:model-value="emitValue" />
            <span>–</span>
            <fwb-select
              v-model="proxyModel[weekday].to"
              :options="buildHoursEnd(weekday)"
              size="sm"
              class="w-28"
              @update:model-value="emitValue" />
          </template>
        </template>
      </div>
      <small v-if="errors[weekday]" class="errors-text">
        {{ errors[weekday].join(', ') }}
      </small>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { FwbSelect, FwbCheckbox } from 'flowbite-vue'
import { capitalize } from '~/javascript/utils/strings'

const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
]

function buildHours() {
  const hours = []
  for (let i = 0; i < 24; i++) {
    const hour12 = i % 12 === 0 ? 12 : i % 12 // Convert 24-hour to 12-hour format
    const suffix = i < 12 ? 'am' : 'pm' // Determine AM/PM
    const hourFormatted = `${hour12}:00 ${suffix}`
    const halfHourFormatted = `${hour12}:30 ${suffix}`
    hours.push({ value: `${i < 10 ? `0${i}` : i}:00`, name: hourFormatted })
    hours.push({ value: `${i < 10 ? `0${i}` : i}:30`, name: halfHourFormatted })
  }
  return hours
}

const HOURS = buildHours()
const DEFAULT_FROM_HOUR = '09:00'
const DEFAULT_TO_HOUR = '18:00'

const emits = defineEmits(['update:modalValue'])

const props = defineProps({
  name: { type: String, required: true },
  modelValue: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) }
})

const proxyModel = ref(props.modelValue)

function buildHoursStart(weekday) {
  const endHourIdx = HOURS.findIndex((hour) => hour.value === proxyModel.value[weekday].to)
  return HOURS.slice(0, endHourIdx)
}

function buildHoursEnd(weekday) {
  const startHourIdx = HOURS.findIndex((hour) => hour.value === proxyModel.value[weekday].from)
  return HOURS.slice(startHourIdx + 1)
}

WEEKDAYS.forEach((weekday) => {
  if (!proxyModel.value[weekday]) {
    proxyModel.value[weekday] = {
      isOpen: true,
      allDay: true,
      from: DEFAULT_FROM_HOUR,
      to: DEFAULT_TO_HOUR
    }
  }
})

const emitValue = () => {
  emits('update:modalValue', props.proxyModel)
}

const onChangeOpenState = (weekday) => {
  if (!proxyModel.value[weekday].from) {
    proxyModel.value[weekday].from = DEFAULT_FROM_HOUR
  }

  if (!proxyModel.value[weekday].to) {
    proxyModel.value[weekday].to = DEFAULT_TO_HOUR
  }

  emitValue()
}

const onChangeAllDayState = (weekday) => {
  if (!proxyModel.value[weekday].from) {
    proxyModel.value[weekday].from = DEFAULT_FROM_HOUR
  }

  if (!proxyModel.value[weekday].to) {
    proxyModel.value[weekday].to = DEFAULT_TO_HOUR
  }

  emitValue()
}

</script>
